import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { SaveOutlined } from '@ant-design/icons';
import { Button, Checkbox, Input, Radio, Space, Tag, Tooltip } from 'antd';
import { CheckboxValueType } from 'antd/es/checkbox/Group';
import _ from 'lodash';

import { deepClone } from '../../../../../../../helpers/utils/deepClone';
import { updateFilterItemInList } from '../../../../../../../helpers/utils/reports';
import { unApi } from '../../../../../api/endpoints/reports/unApi';
import { routeNames } from '../../../../../constants/routes';
import { useActions } from '../../../../../hooks/useActions';
import usePermission from '../../../../../hooks/usePermission';
import { useTypedSelector } from '../../../../../hooks/useTypedSelector';
import ListTextSelector from '../ListTextSelector';

/** Интерфейс элемента списка */
interface ListItem {
  id: string;
  name: string;
}

/** Интерфейс данных фильтра */
interface FilterData {
  items: ListItem[];
  checkeds: ListItem[];
  namePath?: string[];
  /** Сохранённый поисковый запрос для автоматического применения */
  savedSearch?: string;
  [key: string]: unknown;
}

/** Props для LazyListPicker */
interface LazyListPickerProps {
  isModal?: boolean;
  isLocalMode?: boolean;
  localFilters?: any[];
  setLocalFilters?: (filters: any[]) => void;
  disabled?: boolean;
  id: string;
  index?: number;
  showListPlus?: boolean;
  modalVisibleHandler: (visible: boolean) => void;
  isYM?: boolean;
  itemData?: FilterData;
  list?: boolean;
  /** Флаг видимости Popover для сброса состояния при открытии */
  popoverVisible?: boolean;
  /**
   * Готовые значения, известные заранее (напр. кабинеты из справочника UTM-секций).
   * Показываются сразу при открытии фильтра, без ввода подстроки; поиск работает как обычно.
   */
  presetItems?: ListItem[];
  [key: string]: unknown;
}

type LoadStatus = 'start' | 'load' | 'loadtrue' | 'loadfalse';

const LazyListPicker: React.FC<LazyListPickerProps> = ({
  isModal = false,
  isLocalMode = false,
  localFilters,
  setLocalFilters,
  disabled = false,
  id,
  index,
  showListPlus,
  modalVisibleHandler,
  isYM = false,
  itemData,
  list,
  popoverVisible,
  presetItems,
  ...props
}) => {
  const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
  const { filters: globalFilters } = useTypedSelector((state) => 
    isModal ? state.filter.dashboardModal : isYM ? state.filter.yandexMetric : state.filter
  );
  const filters = isLocalMode ? localFilters || [] : globalFilters;
  const { cabinetData } = useTypedSelector((state) => state.adsCabinet);
  const checkPermission = usePermission();
  const { setFilters, setFilter, setMetaFilterUpdate, setMetaChartFetching } = useActions();

  const [filterMode, setFilterMode] = useState(1);
  const [status, setStatus] = useState<LoadStatus>('start');
  const [searchValue, setSearchValue] = useState('');
  // savedSearch инициализируем только в режиме редактирования (когда есть itemData и list)
  const [savedSearch, setSavedSearch] = useState<string | undefined>(
    list && itemData ? itemData.savedSearch : undefined
  );

  // Приводим id к строке для безопасной работы с методами строк
  const safeId = String(id ?? '');

  // Вычисляем ID с учётом режима include/exclude
  const filterId = useMemo(() => {
    if (filterMode === 2) {
      return safeId.startsWith('not_') ? safeId : `not_${safeId}`;
    }
    return safeId.startsWith('not_') ? safeId.replace(/^not_/, '') : safeId;
  }, [safeId, filterMode]);

  // Инициализация режима из id
  useEffect(() => {
    setFilterMode(safeId.startsWith('not_') ? 2 : 1);
  }, [safeId]);

  // Определяем режим редактирования
  const isEditing = useMemo(() => {
    return Boolean(list && itemData);
  }, [list, itemData]);

  // Мемоизированные начальные данные фильтра
  const initialFilter = useMemo((): FilterData => {
    // Режим редактирования — используем данные из itemData
    if (itemData && list) {
      return itemData;
    }
    // Режим создания нового фильтра — берём метаданные из props (name, type, namePath и т.д.),
    // но начинаем с чистых items и checkeds чтобы не подтягивать данные от других фильтров.
    // Исключение — presetItems: короткий список, известный заранее (кабинеты рекламных систем),
    // его показываем сразу, чтобы пользователю не приходилось угадывать подстроку для поиска.
    const { items: _items, checkeds: _checkeds, savedSearch: _savedSearch, ...metadata } = props as FilterData;
    return { items: Array.isArray(presetItems) ? presetItems : [], checkeds: [], ...metadata };
  }, [itemData, list, props, presetItems]);

  // Извлекает ID из name, если он содержится в скобках в конце
  const extractIdFromName = (name: string): string | null => {
    if (!name || typeof name !== 'string') return null;
    const match = name.match(/\((\d+)\)\s*$/);
    return match?.[1] || null;
  };

  // Нормализует id к строке для корректного сравнения
  // Если id невалидный (например, "[object Object]"), пытается извлечь из name
  const normalizeItem = (item: ListItem): ListItem => {
    let id = item.id?.toString() || '';
    
    // Если ID невалидный, пытаемся извлечь из name
    if (id === '[object Object]' || id === '') {
      const extractedId = extractIdFromName(item.name);
      if (extractedId) {
        id = extractedId;
      }
    }
    
    return { ...item, id };
  };

  // Удаляет дубликаты по ID, оставляя первый элемент
  const deduplicateByIdAndName = (items: ListItem[]): ListItem[] => {
    const seen = new Set<string>();
    return items.filter((item) => {
      // Создаём уникальный ключ из ID и name для более точной дедупликации
      const key = `${item.id}__${item.name}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  };

  // Нормализует и дедуплицирует массив элементов
  const normalizeAndDeduplicate = (items: ListItem[]): ListItem[] => {
    return deduplicateByIdAndName(items.map(normalizeItem));
  };

  // Инициализация items с учётом checkeds
  const initialItems = useMemo(() => {
    const safeItems = normalizeAndDeduplicate(Array.isArray(initialFilter.items) ? initialFilter.items : []);
    const safeCheckeds = normalizeAndDeduplicate(Array.isArray(initialFilter.checkeds) ? initialFilter.checkeds : []);
    return [
      ...safeCheckeds,
      ...safeItems.filter((x) => !safeCheckeds.some((y) => String(x.id) === String(y.id))),
    ];
  }, [initialFilter]);

  const [items, setItems] = useState<ListItem[]>(initialItems);
  
  // Нормализуем initialFilter для currentData
  const normalizedInitialFilter = useMemo(() => {
    const safeItems = normalizeAndDeduplicate(Array.isArray(initialFilter.items) ? initialFilter.items : []);
    const safeCheckeds = normalizeAndDeduplicate(Array.isArray(initialFilter.checkeds) ? initialFilter.checkeds : []);
    return {
      ...initialFilter,
      items: safeItems,
      checkeds: safeCheckeds,
    };
  }, [initialFilter]);
  
  const [currentData, setCurrentData] = useState<FilterData>(normalizedInitialFilter);

  // Синхронизация currentData при изменении itemData
  useEffect(() => {
    if (itemData && list) {
      const safeItems = normalizeAndDeduplicate(Array.isArray(itemData.items) ? itemData.items : []);
      const safeCheckeds = normalizeAndDeduplicate(Array.isArray(itemData.checkeds) ? itemData.checkeds : []);
      
      // Нормализуем currentData, чтобы ID были корректными
      setCurrentData({
        ...itemData,
        items: safeItems,
        checkeds: safeCheckeds,
      });
      
      setItems([
        ...safeCheckeds,
        ...safeItems.filter((x) => !safeCheckeds.some((y) => String(x.id) === String(y.id))),
      ]);
      // Синхронизируем savedSearch
      if (itemData.savedSearch !== undefined) {
        setSavedSearch(itemData.savedSearch);
      }
    }
  }, [itemData, list]);

  // Сброс состояния при каждом открытии Popover
  useEffect(() => {
    if (popoverVisible) {
      // Сбрасываем поисковую строку и статус
      setSearchValue('');
      setStatus('start');
      
      // В режиме редактирования восстанавливаем данные из itemData
      if (itemData && list) {
        const safeItems = normalizeAndDeduplicate(Array.isArray(itemData.items) ? itemData.items : []);
        const safeCheckeds = normalizeAndDeduplicate(Array.isArray(itemData.checkeds) ? itemData.checkeds : []);
        setItems([
          ...safeCheckeds,
          ...safeItems.filter((x) => !safeCheckeds.some((y) => String(x.id) === String(y.id))),
        ]);
        // Нормализуем currentData
        setCurrentData({
          ...itemData,
          items: safeItems,
          checkeds: safeCheckeds,
        });
        if (itemData.savedSearch !== undefined) {
          setSavedSearch(itemData.savedSearch);
        }
      } else {
        // В режиме создания нового фильтра — начинаем с чистого состояния,
        // кроме presetItems: короткий список известен заранее и должен быть виден сразу
        const preset = Array.isArray(presetItems) ? presetItems : [];
        setItems(preset);
        const { items: _items, checkeds: _checkeds, savedSearch: _savedSearch, ...metadata } = props as FilterData;
        setCurrentData({ items: preset, checkeds: [], ...metadata });
        setSavedSearch(undefined);
      }
    }
  }, [popoverVisible]);

  // Преобразование ID для дополнительных полей
  const convertIdToAdditionalFieldId = useCallback((baseId: string): string => {
    const getTypeFromNamePath = (path: string[] = []): string => {
      const isExtraField = path.some((p) => p.includes('Доп. поля'));

      if (path.includes('Заявки') && isExtraField) return 'no_app_extra_field_filter';
      if (path.includes('Регистрации') && isExtraField) return 'reg_extra_field_filter';
      if (path.includes('Пользователи') && isExtraField) return 'user_extra_field_filter';
      if (path.includes('Заказы') && isExtraField) return 'order_extra_field_filter';

      return baseId;
    };

    const mappedValue = getTypeFromNamePath(currentData.namePath);

    if (filterMode === 2 && !mappedValue.startsWith('not_')) {
      return `not_${mappedValue}`;
    }
    if (filterMode === 1) {
      return mappedValue.replace(/^not_/, '');
    }
    return mappedValue;
  }, [currentData.namePath, filterMode]);

  const canApply = isEditing || currentData.checkeds.length > 0 || !!savedSearch;

  const onApply = useCallback(() => {
    // Запрещаем добавление нового фильтра без выбранных значений и без сохранённого поиска
    if (!canApply) {
      return;
    }

    const finalId = convertIdToAdditionalFieldId(filterId);
    const _currentData = {
      ...currentData,
      id: finalId,
      savedSearch: savedSearch,
    };

    if (isLocalMode && setLocalFilters) {
      // Локальный режим - используем callback
      if (isEditing && index !== undefined) {
        let _filters = _.cloneDeep(filters);
        _filters = updateFilterItemInList(_filters, index, _currentData);
        setLocalFilters(_filters);
      } else {
        setLocalFilters([...filters, _currentData]);
      }
    } else {
      // Глобальный режим - используем Redux
      if (isEditing && index !== undefined) {
        let _filters = _.cloneDeep(filters);
        _filters = updateFilterItemInList(_filters, index, _currentData);
        setFilters(isModal ? { isModal, data: _filters } : isYM ? { isYM, data: _filters } : _filters);
      } else {
        const isWebinarQuality = window.location.pathname.includes(routeNames.reportWebinarQuality);
        if (isWebinarQuality) {
          setMetaChartFetching({ value: true });
        }
        setFilter({ ..._currentData, isModal, isYM });
      }
      setMetaFilterUpdate({ value: true });
    }

    modalVisibleHandler(false);
  }, [canApply, convertIdToAdditionalFieldId, filterId, currentData, savedSearch, isEditing, index, filters, isLocalMode, setLocalFilters, isModal, isYM, setFilters, setFilter, setMetaFilterUpdate, setMetaChartFetching, modalVisibleHandler]);

  const loadItems = useCallback((value: string) => {
    if (value.length === 0 || !selectedSchool) return;

    setStatus('load');

    unApi
      .getFilterList(parseInt(selectedSchool?.id || '0'), id, value)
      .then((response) => {
        const stringifyIds = (row: { id?: number | string; name: string }): ListItem => ({
          ...row,
          id: row.id?.toString() || '',
          name: row.name,
        });
        
        let filtersData = (deepClone(response.data) as Array<{ id?: number | string; name: string }>)
          .map(stringifyIds)
          .map((item) => ({
            ...item,
            name: item.name.replace(/&nbsp;/g, ' '),
          }));

        const cabinetIds = ['advert_list_ads_cabinet', 'list_ads_cabinet'];
        if (
          (location.pathname.includes('/source/ads') && cabinetIds.includes(id)) ||
          (location.pathname.includes('/advert/rate') &&
            !checkPermission(['owner', 'employee']) &&
            cabinetIds.includes(id))
        ) {
          filtersData = filtersData.filter((item) =>
            cabinetData.some((el) => el.ad_cabinet.includes(item.name))
          );
        }

        const safeCheckeds = normalizeAndDeduplicate(Array.isArray(currentData.checkeds) ? currentData.checkeds : []);
        setItems([
          ...safeCheckeds,
          ...filtersData.filter((x) => !safeCheckeds.some((y) => String(x.id) === String(y.id))),
        ]);

        setStatus(filtersData.length ? 'loadtrue' : 'loadfalse');
      })
      .catch(() => {
        setStatus('loadfalse');
      });
  }, [selectedSchool, id, checkPermission, cabinetData, currentData.checkeds]);

  const onChangeCheck = useCallback((checkedValue: CheckboxValueType[]) => {
    setCurrentData((prevState) => ({
      ...prevState,
      items,
      // Сравниваем как строки для корректной работы с BigInt id
      checkeds: items.filter(({ id }) => checkedValue.some((v) => String(v) === String(id))),
    }));
  }, [items]);

  const onChangeSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value || '';
    setSearchValue(value);

    if (value.trim().length === 0) {
      // Если поиск очищен — возвращаемся в стартовый стейт:
      // показываем только выбранные элементы (если есть) и подсказку.
      const safeCheckeds = Array.isArray(currentData.checkeds) ? currentData.checkeds : [];
      setItems(safeCheckeds);
      setStatus('start');
      return;
    }

    loadItems(value);
  }, [loadItems, currentData.checkeds]);

  const checkAll = useCallback(() => {
    setCurrentData((prevState) => ({
      ...prevState,
      items,
      checkeds: items,
    }));
  }, [items]);

  const onClear = useCallback(() => {
    setCurrentData((prevState) => ({
      ...prevState,
      checkeds: [],
    }));
  }, []);

  // Функция для установки выбранных элементов из ListTextSelector
  // Только обновляет checkeds, не трогает items (items устанавливаются через setList/setMainList)
  const setCheckeds = useCallback((checkeds: ListItem[]) => {
    // Нормализуем новые checkeds
    const normalizedCheckeds = normalizeAndDeduplicate(checkeds);
    
    setCurrentData((prevState) => ({
      ...prevState,
      checkeds: normalizedCheckeds,
    }));
  }, []);

  // Функция для установки списка элементов из ListTextSelector
  // Пустой массив очищает, непустой — заменяет текущие items, сохраняя ранее выбранные
  const setList = useCallback((newItems: ListItem[]) => {
    if (newItems.length === 0) {
      // Очистка — сбрасываем items
      setCurrentData((prevState) => ({
        ...prevState,
        items: [],
      }));
      setItems([]);
      return;
    }
    // Нормализуем элементы для корректного сравнения ID
    const normalizedItems = normalizeAndDeduplicate(newItems);
    
    setCurrentData((prevState) => {
      // Сохраняем ранее выбранные элементы, которых нет в новом списке
      const existingCheckeds = normalizeAndDeduplicate(prevState.checkeds || []);
      const newItemIds = new Set(normalizedItems.map((item) => String(item.id)));
      const checkedsNotInNew = existingCheckeds.filter((item) => !newItemIds.has(String(item.id)));
      
      // Объединяем: сначала ранее выбранные (которых нет в новом списке), потом новые
      const combinedItems = [...checkedsNotInNew, ...normalizedItems];
      
      // Также обновляем items state синхронно
      setItems(combinedItems);
      
      return {
        ...prevState,
        items: combinedItems,
      };
    });
  }, []);

  // Обработчик сохранения поискового запроса
  const handleSaveSearch = useCallback(() => {
    if (!searchValue.trim()) return;
    
    // Сохраняем поисковый запрос
    setSavedSearch(searchValue.trim());
    
    // Выбираем все найденные элементы (если есть)
    setCurrentData((prevState) => ({
      ...prevState,
      items: items.length > 0 ? items : prevState.items,
      checkeds: items.length > 0 ? items : prevState.checkeds,
      savedSearch: searchValue.trim(),
    }));
  }, [searchValue, items]);

  // Обработчик удаления сохранённого поиска
  const handleRemoveSavedSearch = useCallback(() => {
    setSavedSearch(undefined);
    setCurrentData((prevState) => ({
      ...prevState,
      savedSearch: undefined,
    }));
  }, []);

  // Мемоизированные значения для Checkbox.Group
  // Нормализуем ID и фильтруем только те, которые есть в текущем списке items
  const checkboxGroupValue = useMemo(() => {
    const itemIds = new Set(items.map((item) => String(item.id)));
    return currentData.checkeds
      .map((item) => normalizeItem(item).id)
      .filter((id) => itemIds.has(String(id)));
  }, [currentData.checkeds, items]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !disabled && canApply) {
      e.preventDefault();
      onApply();
    }
  }, [disabled, canApply, onApply]);

  return (
    <div className="picker-container" onKeyDown={handleKeyDown}>
      <Input
        onChange={onChangeSearch}
        placeholder="Найти"
        allowClear
        disabled={disabled}
        value={searchValue}
        suffix={
          searchValue.trim() && (status === 'loadtrue' || status === 'loadfalse') ? (
            <Tooltip 
              title={<span style={{ fontSize: '12px' }}>Сохранить поиск для автоматического выбора подходящих значений</span>}
            >
              <SaveOutlined
                onClick={handleSaveSearch}
                style={{ cursor: 'pointer', color: '#1890ff' }}
              />
            </Tooltip>
          ) : null
        }
      />

      {savedSearch && (
        <div className="picker-saved-search">
          <Tag
            closable
            onClose={handleRemoveSavedSearch}
            color="blue"
          >
            {savedSearch}
          </Tag>
        </div>
      )}

      <div className="picker-radio-group">
        <Radio.Group
          disabled={disabled}
          onChange={(e) => setFilterMode(e.target.value)}
          value={filterMode}
        >
          <Radio value={1}>Включить</Radio>
          <Radio value={2}>Исключить</Radio>
        </Radio.Group>
      </div>

      <div className="picker-buttons">
        <Button disabled={disabled} onClick={checkAll} size="small">
          Выбрать все
        </Button>

        <ListTextSelector
          showListPlus={showListPlus}
          id={id as unknown as number}
          setList={setCheckeds}
          setMainList={setList}
        />

        <Button disabled={disabled} onClick={onClear} size="small">
          Очистить
        </Button>
      </div>

      <div className="picker-list picker-list-lazy">
        {items.length === 0 && status === 'start' && (
          <div className="picker-message">
            Начните вводить в поиске,
            <br />
            чтобы получить данные.
          </div>
        )}
        {status === 'loadfalse' && searchValue.trim().length > 0 && (
          <div className="picker-message">
            По вашему запросу ничего не найдено
          </div>
        )}

        <Checkbox.Group
          disabled={disabled}
          value={checkboxGroupValue}
          onChange={onChangeCheck}
        >
          <Space direction="vertical">
            {items.map((item) => (
              <Checkbox
                key={`${item.id}_${item.name}`}
                value={item.id}
                style={{ marginRight: 7 }}
                onClick={(e) => {
                  // Убираем фокус с чекбокса после клика для избежания визуальных артефактов
                  (e.target as HTMLElement).blur();
                }}
              >
                {item.name.replace(/&nbsp;/g, '\u00A0')}
              </Checkbox>
            ))}
          </Space>
        </Checkbox.Group>
      </div>

      <Button
        disabled={disabled || !canApply}
        type="primary"
        className="picker-apply-btn"
        onClick={onApply}
      >
        Применить
      </Button>
    </div>
  );
};

export default LazyListPicker;
